import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, Spinner } from "@nextui-org/react";
import ReactMapGL, { Marker } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

export default function LocationPicker({ isOpen, onClose, onLocationSelect, initialLatitude = -1.3911261987874894, initialLongitude = 113.4216565328876, initialZoom = 5 }) {
  const [viewport, setViewport] = useState({
    latitude: initialLatitude,
    longitude: initialLongitude,
    zoom: initialZoom,
  });

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [locationDetails, setLocationDetails] = useState(null);

  // Handle map click to select location
  const handleMapClick = async (event) => {
    const { lng, lat } = event.lngLat;

    setSelectedLocation({
      longitude: lng,
      latitude: lat,
    });

    // Fetch reverse geocoding information
    await fetchLocationDetails(lng, lat);
  };

  // Fetch location details using Mapbox Geocoding API
  const fetchLocationDetails = async (longitude, latitude) => {
    setIsLoading(true);
    try {
      const accessToken = process.env.REACT_APP_MAP_BOX_API_KEY;
      const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${accessToken}&types=place,district,region,country&language=id`);

      if (response.ok) {
        const data = await response.json();
        const features = data.features;

        // Parse the response to extract location information
        const locationInfo = {
          longitude: longitude,
          latitude: latitude,
          address: features[0]?.place_name || "",
          village: null,
          district: null,
          regency: null,
          province: null,
          country: null,
        };

        // Extract administrative information from features
        features.forEach((feature) => {
          const placeTypes = feature.place_type || [];

          // Priority: region untuk province
          if (placeTypes.includes("region")) {
            locationInfo.province = feature.text;
          }

          // Priority: place untuk regency (kota/kabupaten)
          if (placeTypes.includes("place")) {
            // Cek apakah ini kota/kabupaten (biasanya memiliki wikidata)
            if (feature.properties?.wikidata) {
              locationInfo.regency = feature.text;
            }
          }

          // Handle district
          if (placeTypes.includes("district")) {
            locationInfo.district = feature.text;
          }

          // Handle country
          if (placeTypes.includes("country")) {
            locationInfo.country = feature.text;
          }
        });

        // Jika tidak ada regency dari place, coba ambil dari context
        if (!locationInfo.regency && features[0]?.context) {
          const placeContext = features[0].context.find((ctx) => ctx.id.startsWith("place.") && ctx.wikidata);
          if (placeContext) {
            locationInfo.regency = placeContext.text;
          }
        }

        // Jika tidak ada province dari region, coba ambil dari context
        if (!locationInfo.province && features[0]?.context) {
          const regionContext = features[0].context.find((ctx) => ctx.id.startsWith("region."));
          if (regionContext) {
            locationInfo.province = regionContext.text;
          }
        }

        setLocationDetails(locationInfo);
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
      // Set basic location info even if reverse geocoding fails
      setLocationDetails({
        longitude: longitude,
        latitude: latitude,
        address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        village: null,
        district: null,
        regency: null,
        province: null,
        country: null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle location confirmation
  const handleConfirmLocation = () => {
    if (selectedLocation && locationDetails) {
      onLocationSelect(locationDetails);
      handleClose();
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedLocation(null);
    setLocationDetails(null);
    onClose();
  };

  return (
    <Modal size="5xl" isOpen={isOpen} onClose={handleClose} scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col">
            <h2>Pilih Lokasi</h2>
            <p className="text-sm text-ink-500">Klik pada peta untuk memilih lokasi</p>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="w-full h-[500px] relative">
            <ReactMapGL
              {...viewport}
              width="100%"
              height="100%"
              mapboxAccessToken="pk.eyJ1Ijoic2liMy1rbGhrIiwiYSI6ImNtMms1ZWVrZjAxb3MybHEyajE5cG5zb28ifQ.aCs5cuGJhZqytwo7zlVl5w"
              mapStyle="mapbox://styles/sib3-klhk/cm3i4eidd00dn01s9809ze69i"
              onMove={(evt) => setViewport(evt.viewState)}
              onClick={handleMapClick}
              dragPan={true}
              scrollZoom={true}
              doubleClickZoom={true}
              touchZoom={true}
            >
              {selectedLocation && (
                <Marker latitude={selectedLocation.latitude} longitude={selectedLocation.longitude}>
                  <div
                    style={{
                      fontSize: "24px",
                      color: "#C03A2B",
                      cursor: "pointer",
                    }}
                  >
                    📍
                  </div>
                </Marker>
              )}
            </ReactMapGL>
          </div>

          {/* Location Information Display */}
          {selectedLocation && (
            <Card className="mt-4">
              <CardBody>
                <div className="space-y-2">
                  <h3 className="font-semibold">Informasi Lokasi Terpilih:</h3>

                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span>Mengambil informasi lokasi...</span>
                    </div>
                  ) : locationDetails ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <strong>Latitude:</strong> {locationDetails.latitude.toFixed(6)}
                      </div>
                      <div>
                        <strong>Longitude:</strong> {locationDetails.longitude.toFixed(6)}
                      </div>
                      {locationDetails.address && (
                        <div className="col-span-2">
                          <strong>Alamat:</strong> {locationDetails.address}
                        </div>
                      )}
                      {locationDetails.village && (
                        <div>
                          <strong>Desa/Kelurahan:</strong> {locationDetails.village}
                        </div>
                      )}
                      {locationDetails.district && (
                        <div>
                          <strong>Kecamatan:</strong> {locationDetails.district}
                        </div>
                      )}
                      {locationDetails.regency && (
                        <div>
                          <strong>Kabupaten/Kota:</strong> {locationDetails.regency}
                        </div>
                      )}
                      {locationDetails.province && (
                        <div>
                          <strong>Provinsi:</strong> {locationDetails.province}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="danger" variant="light" onPress={handleClose}>
            Batal
          </Button>
          <Button color="primary" onPress={handleConfirmLocation} isDisabled={!selectedLocation || isLoading}>
            Pilih Lokasi
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
